'use client';

import { useGetDrawResult } from '@svs/nova-poolbetting/core/frameworks/nextjs/draw';
import type { ResultFilter } from '@svs/nova-poolbetting/core/lib/client';
import { useBestBetRows } from '@svs/nova-poolbetting/core/nextjs/wager/client';
import { type Outcome, toOutcome } from '@svs/nova-poolbetting/schema/coupon';
import type { DrawNumber } from '@svs/nova-poolbetting/schema/draw';
import { get1X2Outcome, type MultiForecast, type MultiForecastOutcome } from '@svs/nova-poolbetting/schema/forecast';
import type { Definition } from '@svs/nova-poolbetting/schema/frameworks';
import type { WagerId } from '@svs/nova-poolbetting/schema/wager';
import { Card } from '@svs/ui/react/card';
import type { ReactNode } from 'react';

import { MultiForecastContainer } from './MultiForecastContainer';
import { WinDivisions } from './WinDivisions';

type WinDivisionsContainerClientProps = {
  product: Definition['drawProducts'][number];
  drawNumber: DrawNumber;
  wagerId: WagerId;
  hasMaskedEvents?: boolean;
  multiForecastOutcome?: MultiForecastOutcome;
  isMarketplaceWager?: boolean;
};

/**
 * WinDivisionsContainer client component.
 *
 * @param root0 props
 * @param root0.product product for current draw
 * @param root0.drawNumber draw number for current draw
 * @param root0.wagerId wager ID for current wager
 * @param root0.hasMaskedEvents indicates if events are masked
 * @param root0.multiForecastOutcome The multi-forecast outcome selected
 * @param root0.isMarketplaceWager Indicates if the wager is a marketplace wager
 * @returns The WinDivisions presentational component.
 * @throws {Error} if draw data fails to load
 */
// eslint-disable-next-line complexity
export function WinDivisionsContainerClient({
  product,
  drawNumber,
  wagerId,
  hasMaskedEvents,
  multiForecastOutcome,
  isMarketplaceWager,
}: WinDivisionsContainerClientProps): ReactNode {
  const drawResults = useGetDrawResult({ product, drawNumber });
  let resultFilter: ResultFilter | undefined = undefined;

  let multiForecast: undefined | MultiForecast[] = undefined;
  let currentCorrectResult: undefined | Outcome = undefined;

  const { data, isError } = drawResults;
  if (data.type === 'FORECAST' && data.data.multiForecast) {
    multiForecast = data.data.multiForecast;
    const [firstForecast] = data.data.multiForecast;
    if (firstForecast) {
      const drawResult = data.data.drawResults[firstForecast.eventNumber - 1];
      currentCorrectResult = drawResult?.outcome[0];
    }
  }

  if (multiForecastOutcome !== undefined && data.type !== 'NONE') {
    const results = data.data.drawResults;
    resultFilter = results.map((outcome) => outcome.outcome);
    const eventNumber = multiForecast ? multiForecast[0]?.eventNumber : 0;
    resultFilter[eventNumber ? eventNumber - 1 : 0] = [toOutcome(multiForecastOutcome)];
  }
  const bestBetRows = useBestBetRows(wagerId, resultFilter);

  if (isError) {
    throw new Error('Failed to load draw data');
  }

  if (data.type === 'NONE') {
    return undefined;
  }
  const { drawResults: drawResultsEvents, winDivisions } = data.data;
  const numberOfEvents = drawResultsEvents.length;

  const matchedMultiForecastWinDivisions =
    multiForecast && multiForecastOutcome !== undefined
      ? multiForecast.filter(
          (forecast) => get1X2Outcome({ outcome: forecast.outcome })[0] === toOutcome(multiForecastOutcome)
        )
      : undefined;

  const winDivisionsToUse =
    matchedMultiForecastWinDivisions && matchedMultiForecastWinDivisions.length > 0
      ? matchedMultiForecastWinDivisions
      : winDivisions;

  return (
    <section aria-label="Vinstgrupper" className="mt-5 mb-5">
      <Card className="p-0">
        {multiForecast && (
          <MultiForecastContainer
            multiForecast={multiForecast}
            currentCorrectResult={currentCorrectResult}
            drawNumber={drawNumber}
            product={product}
            selectedOutcome={multiForecastOutcome}
          />
        )}
        <WinDivisions
          winDivisions={winDivisionsToUse}
          numberOfEvents={numberOfEvents}
          bestBetRows={bestBetRows.data}
          hasMaskedEvents={hasMaskedEvents}
          shouldHideBackground={multiForecastOutcome !== currentCorrectResult || hasMaskedEvents}
          isMarketplaceWager={isMarketplaceWager}
        />
      </Card>
    </section>
  );
}
